/**
 * Composite Analyzer: Confidence (Conviction) Calibration — setup scope.
 *
 * Reads the 1-5 `trade.conviction` rating set at entry time and computes
 * per-tier WR + avg R for trades on this setup. Three patterns surface:
 *
 *   1. Low-conviction leak — trades rated 1-2 conviction WR is meaningfully
 *      worse than 4-5. The trader correctly self-rates "I'm not sure" but
 *      takes the trade anyway. Surfacing the data closes the feedback loop.
 *
 *   2. High-conviction edge — trades rated 4-5 conviction WR meaningfully
 *      higher than the rest. Confirms the trader's judgment is real signal.
 *      Praise + suggestion to weight toward high-conviction entries.
 *
 *   3. Inverted calibration — high-conviction tier wins LESS than low.
 *      Rare but meaningful: it means the trader's confidence isn't calibrated
 *      to actual edge. Could be FOMO masquerading as conviction, or a setup
 *      the trader has the wrong mental model for.
 *
 * Trades with no conviction rating are silently excluded (the field is
 * optional). No nag for traders who don't self-rate; just signal for the
 * ones who do.
 *
 * Reads:
 *   trade.conviction — 1-5 (or '' / undefined for unrated)
 *   trade.netPnl     — must be enriched by scope
 *   trade.rMultiple  — for avg R per tier
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';

export const key = 'convictionCalibration';
export const tier = 'free';

// Sample-size guard — need this many rated trades on each side of the
// comparison to call the difference signal.
const MIN_TRADES_PER_TIER_GROUP = 4;
// WR gap that constitutes a "meaningful" calibration signal
const MIN_WR_GAP = 0.20;

const fmtPct = (n) => `${Math.round(n * 100)}%`;
const fmtR = (n) => Number.isFinite(n) ? `${n >= 0 ? '+' : ''}${n.toFixed(2)}R` : '—';

const isWin = (t) => Number.isFinite(t.netPnl) && t.netPnl > 0;

/**
 * Parse + validate a trade's conviction. Returns 1-5 or null. Strings,
 * numbers, and floats all coerced; out-of-range values rejected.
 */
const convictionOf = (trade) => {
  const raw = trade?.conviction;
  if (raw === '' || raw == null) return null;
  const n = Math.round(parseFloat(raw));
  if (!Number.isFinite(n)) return null;
  if (n < 1 || n > 5) return null;
  return n;
};

/**
 * Bucket conviction levels into "low" (1-2) and "high" (4-5). 3 is
 * "neutral" and excluded from the gap comparison so we don't blur the
 * signal with mid-conviction trades.
 */
const tierOf = (conviction) => {
  if (conviction == null) return null;
  if (conviction <= 2) return 'low';
  if (conviction >= 4) return 'high';
  return 'neutral';
};

const bucketStats = (trades) => {
  const wins = trades.filter(isWin).length;
  const wr = trades.length > 0 ? wins / trades.length : 0;
  const rs = trades.map(t => parseFloat(t.rMultiple)).filter(Number.isFinite);
  const avgR = rs.length > 0 ? rs.reduce((s, r) => s + r, 0) / rs.length : null;
  return { count: trades.length, wins, wr, avgR };
};

export const analyze = (ctx) => {
  const { setupName, setupTrades } = ctx || {};
  if (!setupName || !Array.isArray(setupTrades) || setupTrades.length === 0) return null;

  // Live-trade gate: tier WR comparison (high-conviction vs low-conviction)
  // depends on realized outcomes. Open trades' unrealized P&L isn't a
  // realized outcome — including them would fire a premature "conviction
  // inverted" or "calibrated" claim while the user is still in the trade
  // (panic-exit risk on inverted, premature celebration on calibrated).
  // Matches summarizePnl semantics.
  const closedSetupTrades = setupTrades.filter(t => t?.isOpen !== true);
  if (closedSetupTrades.length === 0) return null;

  // Bucket trades by tier. Drop unrated.
  const low = [];
  const high = [];
  for (const t of closedSetupTrades) {
    const c = convictionOf(t);
    const tier = tierOf(c);
    if (tier === 'low') low.push(t);
    else if (tier === 'high') high.push(t);
    // neutral and unrated: skip
  }
  if (low.length < MIN_TRADES_PER_TIER_GROUP || high.length < MIN_TRADES_PER_TIER_GROUP) return null;

  const lowStats  = bucketStats(low);
  const highStats = bucketStats(high);
  const gap = highStats.wr - lowStats.wr;

  if (Math.abs(gap) < MIN_WR_GAP) return null;

  // Inverted calibration — high-conviction tier loses MORE. Rare and worth flagging
  // separately because the framing is different ("your confidence isn't calibrated").
  if (gap < 0) {
    return makeObservation({
      id: `${key}:${setupName}:inverted`,
      analyzer: key,
      scope: 'setup',
      scopeId: setupName,
      severity: SEVERITY.WARNING,
      category: CATEGORY.PROCESS,
      title: `"${setupName}" conviction is INVERTED — high-conviction wins ${fmtPct(highStats.wr)}, low-conviction wins ${fmtPct(lowStats.wr)}`,
      detail: `${highStats.wins}/${highStats.count} winners on conviction-4-5 trades. ${lowStats.wins}/${lowStats.count} winners on conviction-1-2 trades. Either FOMO is masquerading as conviction, or your mental model for what makes this setup "high-conviction" is the opposite of what actually works.`,
      action: `Pull a recent conviction-5 ${setupName} loser side by side with a conviction-2 ${setupName} winner. What did the loser have that the winner didn't? That's your actual edge — your gut is reading it backwards.`,
      evidence: {
        lowCount: lowStats.count, lowWR: lowStats.wr, lowAvgR: lowStats.avgR,
        highCount: highStats.count, highWR: highStats.wr, highAvgR: highStats.avgR,
        gap,
      },
      confidence: 0.78,
      priority: 86,
    });
  }

  // Standard calibration gap — high beats low by ≥ MIN_WR_GAP. Strong
  // positive feedback that the trader's self-rating IS predictive.
  return makeObservation({
    id: `${key}:${setupName}:calibrated`,
    analyzer: key,
    scope: 'setup',
    scopeId: setupName,
    severity: SEVERITY.SUCCESS,
    category: CATEGORY.PROCESS,
    title: `"${setupName}" conviction is calibrated — high-conviction wins ${fmtPct(highStats.wr)} vs ${fmtPct(lowStats.wr)} on low`,
    detail: `${highStats.wins}/${highStats.count} winners on conviction-4-5 trades${highStats.avgR != null ? ` (avg ${fmtR(highStats.avgR)})` : ''}. ${lowStats.wins}/${lowStats.count} winners on conviction-1-2 trades${lowStats.avgR != null ? ` (avg ${fmtR(lowStats.avgR)})` : ''}. Your gut is reading the setup correctly — the data backs your self-rating.`,
    action: `Lean harder on the calibration: when conviction is 1-2 on this setup, the data says PASS — let the trade go and wait for the high-conviction read.`,
    evidence: {
      lowCount: lowStats.count, lowWR: lowStats.wr, lowAvgR: lowStats.avgR,
      highCount: highStats.count, highWR: highStats.wr, highAvgR: highStats.avgR,
      gap,
    },
    confidence: 0.8,
    priority: 78,
  });
};

// Test-friendly exports
export {
  convictionOf,
  tierOf,
  bucketStats,
  MIN_TRADES_PER_TIER_GROUP,
  MIN_WR_GAP,
};
